import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;
  const isAdmin = user?.level === 'Admin';

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      if (response.success && response.token && response.user) {
        // Calcular la fecha de expiración basada en el expires_at del servidor
        const expirationDate = new Date(response.expires_at * 1000); // expires_at viene en timestamp Unix
        const now = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
        
        // Guardar el token con el tiempo de vida exacto del servidor
        Cookies.set('auth_token', response.token, { 
          expires: daysUntilExpiration > 0 ? daysUntilExpiration : 1 // Mínimo 1 día si el cálculo da 0 o negativo
        });
        
        // También guardar la fecha de expiración para referencia
        Cookies.set('token_expires_at', response.expires_at, {
          expires: daysUntilExpiration > 0 ? daysUntilExpiration : 1
        });
        
        // Guardar información del usuario en cookies para persistencia (incluyendo balance)
        Cookies.set('user_data', JSON.stringify(response.user), {
          expires: daysUntilExpiration > 0 ? daysUntilExpiration : 1
        });
        
        setUser(response.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    authService.logout().catch(console.error);
    Cookies.remove('auth_token');
    Cookies.remove('token_expires_at');
    Cookies.remove('user_data');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.me();
      const userInfo = userData.user || userData;
      
      // Si no tiene balance en la respuesta de /me, mantener el balance del login
      if (!userInfo.balance && user?.balance) {
        userInfo.balance = user.balance;
      }
      
      setUser(userInfo);
      
      // Actualizar la cookie con los datos más recientes
      const expiresAt = Cookies.get('token_expires_at');
      if (expiresAt) {
        const expirationDate = new Date(parseInt(expiresAt) * 1000);
        const now = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiration > 0) {
          Cookies.set('user_data', JSON.stringify(userInfo), {
            expires: daysUntilExpiration
          });
        }
      }
      
      return userInfo;
    } catch (error) {
      console.error('Error refreshing user:', error);
      // No hacer logout automáticamente aquí, dejar que el componente padre decida
      throw error;
    }
  };

  // Función para actualizar el balance del usuario
  const updateUserBalance = (newBalance) => {
    if (user) {
      const updatedUser = { ...user, balance: newBalance };
      setUser(updatedUser);
      
      // Actualizar también la cookie
      const expiresAt = Cookies.get('token_expires_at');
      if (expiresAt) {
        const expirationDate = new Date(parseInt(expiresAt) * 1000);
        const now = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiration > 0) {
          Cookies.set('user_data', JSON.stringify(updatedUser), {
            expires: daysUntilExpiration
          });
        }
      }
    }
  };

  // Función para verificar si el token ha expirado
  const isTokenExpired = () => {
    const expiresAt = Cookies.get('token_expires_at');
    if (!expiresAt) return true;
    
    const expirationTime = parseInt(expiresAt) * 1000; // Convertir a milisegundos
    return Date.now() >= expirationTime;
  };

  // Función para restaurar el usuario desde las cookies
  const restoreUserFromCookies = () => {
    const userData = Cookies.get('user_data');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        return true;
      } catch (error) {
        console.error('Error parsing user data from cookies:', error);
        Cookies.remove('user_data');
      }
    }
    return false;
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = Cookies.get('auth_token');
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Verificar si el token ha expirado antes de intentar usarlo
      if (isTokenExpired()) {
        console.log('Token expirado, limpiando cookies');
        logout();
        setIsLoading(false);
        return;
      }

      // Primero restaurar el usuario desde las cookies para evitar el parpadeo
      const userRestored = restoreUserFromCookies();
      
      if (userRestored) {
        // Si se restauró el usuario, establecer isLoading a false inmediatamente
        setIsLoading(false);
        
        // Luego hacer la verificación en segundo plano para actualizar datos
        try {
          await refreshUser();
        } catch (error) {
          console.error('Error al refrescar usuario en segundo plano:', error);
          // Si falla la verificación en segundo plano, mantener el usuario restaurado
          // Solo hacer logout si es un error de autenticación específico
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            logout();
          }
        }
      } else {
        // Si no se pudo restaurar desde cookies, intentar verificar con el servidor
        try {
          await refreshUser();
          setIsLoading(false);
        } catch (error) {
          console.error('Error de inicialización de autenticación:', error);
          logout();
          setIsLoading(false);
        }
      }
    };

    initAuth();
  }, []);

  // Verificar periódicamente si el token ha expirado (solo cada 5 minutos para reducir overhead)
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired()) {
        console.log('Token expirado durante la sesión, cerrando sesión');
        logout();
      }
    };

    // Verificar cada 5 minutos si el token ha expirado
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    refreshUser,
    updateUserBalance, // Nueva función para actualizar el balance
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};